import * as React from 'react';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import { Spacings, mediaQuery } from '@styles/index';
import H3 from '@base/H3/H3';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import { getEventMeta } from '@utils/cmsEventDedup';
import P2 from '@base/P2/P2';
import { useSelector } from 'react-redux';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import {
  PLATFORM_BATCH_CARD_CLICKED,
  PLATFORM_BATCH_VIEWED
} from '@constants/segment';
import { segment } from '@actions/analytics';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import useGoalEventProps from '@hooks/useGoalEventProps';

const Wrapper = styled(Paper)`
  width: 100%;
  grid-column: span 18;
  position: relative;
  margin-top: ${Spacings.SPACING_7B};
  margin-bottom: ${Spacings.SPACING_12B};
  padding: ${Spacings.SPACING_8B};
`;

const Header = styled(H3)`
  margin-bottom: ${Spacings.SPACING_2B};
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-width: 500px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const Background = styled.div`
  position: absolute;
  right: 0;
  bottom: -${Spacings.SPACING_1B};
`;

const ViewButton = styled(Button)`
  color: var(--color-base-1);
  width: 100px;
  min-height: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
  font-size: 14px;
`;

const StyP2 = styled(P2)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_6B};
`;

const RecordedContentBanner = ({ offering, goalSlug, goalUID }) => {
  const { isMobile } = useWindowSize();
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const { offeringInfo, offeringUid, offeringType } = offering;
  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });
  const segmentData = usePlatformEventProps({
    batch: {
      uid: offeringUid,
      batchUID: offeringUid,
      name: offeringInfo.title,
      startsAt: 'NA',
      completedAt: 'NA'
    },
    lastPrimarySourceSection:
      'NEET UG Recorded Content Touch point in Browse page',
    isBatch: true
  });

  React.useEffect(() => {
    const trackBatchViewed = async () => {
      const { send_to_crm } = await getEventMeta(PLATFORM_BATCH_VIEWED, {
        goal_uid: goalSegmentData?.goal_uid
      });
      segment.track(PLATFORM_BATCH_VIEWED, {
        ...goalSegmentData,
        ...segmentData,
        firstPrimarySource: 'Browse Page',
        batch_type: 'Lite',
        send_to_crm
      });
    };
    trackBatchViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onViewClick = () => {
    segment.track(PLATFORM_BATCH_CARD_CLICKED, {
      ...goalSegmentData,
      ...segmentData,
      firstPrimarySource: 'Browse Page',
      batch_type: 'Lite'
    });
    pushRoute({
      pathname: '/cluster/[slug]/[offeringUID]',
      query: {
        slug: offeringInfo.title,
        offeringUID: offeringUid,
        goalUID,
        goalSlug,
        offeringType
      }
    });
  };

  return (
    <Wrapper>
      <Header>{offeringInfo.title}</Header>
      <StyP2>{offeringInfo.description}</StyP2>

      <ViewButton // @ts-ignore
        label={offeringInfo.ctaText}
        color="black"
        onClick={onViewClick}
      />
      {!isMobile && (
        <Background>
          <Image
            src={
              isDarkModeOn
                ? offeringInfo.image.webUrl
                : offeringInfo.image.webUrlDark
            }
            height={182}
            width={280}
            alt="background"
          />
        </Background>
      )}
    </Wrapper>
  );
};

export default RecordedContentBanner;
